import React from "react";

import { useTheme } from "@/components/ui/theme-provider";
import { isOplotFlavor } from "@/env/env";
import { cn } from "@/lib/utils";

import recoveryIllustrationDark from "./forgot-password-illustration-nodeshift-dark.webp";
import recoveryIllustrationLight from "./forgot-password-illustration-nodeshift-light.webp";
import { shouldShowAuthIllustration } from "./utils";

// NOTE: NSAI-6144 — only the NodeShift pair is referenced now. This used to
// pick a neutral blue variant on a flavored build, but a flavored build no
// longer paints anything here at all, so that branch could never be reached.
// `forgot-password-illustration-generic-{light,dark}.webp` are deliberately
// kept on disk unreferenced rather than deleted with the branch: NSAI-6016 may
// yet give a flavored build artwork of its own, and restoring the swap is then
// a ternary here rather than an asset hunt through history.
const RECOVERY_ILLUSTRATIONS = {
  light: recoveryIllustrationLight,
  dark: recoveryIllustrationDark,
};

interface ICenterCardLayoutProps extends React.PropsWithChildren {
  className?: string;
}

export const CenterCardLayout = ({
  className,
  children,
}: ICenterCardLayoutProps): React.ReactElement => {
  const { hasCustomBranding, isBrandingResolved } = useTheme();

  // NOTE: The illustration belongs to the default appearance. An organization
  // that configured its own appearance gets a plain card and its own logo
  // instead, so the NodeShift artwork never sits behind someone else's
  // branding. Same rule as AuthLayout applies to sign in / sign up, including
  // the wait for the branding lookup: an unresolved hasCustomBranding is not a
  // confirmed default appearance, so the artwork stays out until it resolves.
  //
  // NOTE: NSAI-6144 — the flavor gate is the same one AuthLayout applies, and it
  // covers every screen this layout backs: password recovery and reset, accept
  // invite, the shared page and the public share / public chat state screens. A
  // white-label build shows a plain card on all of them.
  const isIllustrationVisible = shouldShowAuthIllustration({
    isFlavorGated: isOplotFlavor(),
    isBrandingResolved,
    hasCustomBranding,
  });

  return (
    // NOTE: NSAI-5912 — `min-h-dvh`, never `h-screen`. Two reasons, and the
    // shell has to satisfy both because a public share link opens this card in
    // a mobile browser with no session and no navigation:
    //
    // 1. `dvh` over `vh`. `100vh` is the *large* viewport — it deliberately
    //    excludes the URL bar and the bottom toolbar rather than adapting to
    //    them, so the painted area and the visible area disagreed by exactly
    //    the height of the browser chrome. That is what left untinted strips
    //    top and bottom and stopped the illustration short of the bottom.
    // 2. `min-h`, not `h`. A fixed height smaller than its own content makes
    //    `items-center` split the overflow across *both* edges, and the half
    //    above offset 0 is unreachable — scrolling cannot reach it. That is the
    //    keyboard case: the keyboard shrinks the viewport under a card that
    //    does not shrink, and the logo and title clip off the top for good.
    //    `min-h` only ever grows, so centring simply stops applying once the
    //    card outgrows the viewport, which is exactly when it must scroll
    //    instead. Mobile's auth screens reached the same conclusion in
    //    NSAI-4764 (`contentContainerClassName="grow justify-center"`).
    //
    // `w-full` rather than `w-screen`: now that the page can scroll, `100vw`
    // would count a classic vertical scrollbar's gutter as page width and push
    // the document sideways.
    <div
      data-testid="center-card-layout"
      className={cn(
        "min-h-dvh w-full flex items-center justify-center relative",
        className,
      )}
    >
      {isIllustrationVisible && (
        <div
          data-testid="center-card-layout_illustration"
          className="absolute top-0 left-0 w-full h-full -z-10 bg-cover bg-center [background-image:var(--recovery-illustration-light)] dark:[background-image:var(--recovery-illustration-dark)]"
          style={
            {
              "--recovery-illustration-light": `url("${RECOVERY_ILLUSTRATIONS.light}")`,
              "--recovery-illustration-dark": `url("${RECOVERY_ILLUSTRATIONS.dark}")`,
              // NOTE: We need to cast this because the style object is not typed to pass custom properties
            } as React.CSSProperties
          }
        />
      )}
      {/* NOTE: `m-4`, not `mx-4` — the vertical half is what keeps the card off
          the viewport edges once it has outgrown them and the shell has started
          to scroll (see the layout note above). While it fits, the margin is
          absorbed by the centring and changes nothing. */}
      <div
        data-testid="center-card-layout_card"
        className="w-full max-w-lg bg-background rounded-lg px-6 py-8 m-4"
      >
        {children}
      </div>
    </div>
  );
};
